import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";
import { COUNTRIES, estimate, eur, UNITARY_PATENT_FEES } from "@/lib/renewal-fees";

const EPO_PENDING_FEES: { year: string; fee: number }[] = [
  { year: "3rd year", fee: 610 },
  { year: "4th year", fee: 760 },
  { year: "5th year", fee: 1065 },
  { year: "6th year", fee: 1360 },
  { year: "7th year", fee: 1510 },
  { year: "8th year", fee: 1650 },
  { year: "9th year", fee: 1780 },
  { year: "10th year and later", fee: 2015 },
];

const FAQS = [
  {
    q: "What is an EPO renewal fee?",
    a: "A renewal fee (annuity) paid to the European Patent Office each year while a European patent application is still pending. It falls due on the last day of the month containing the anniversary of the filing date, starting with the third year.",
  },
  {
    q: "When do EPO renewal fees stop and national annuities start?",
    a: "The EPO collects renewal fees only while the application is pending. Once the patent is granted, renewal fees are paid to each national office where the patent is validated — or, if you request unitary effect, as a single Unitary Patent renewal fee to the EPO.",
  },
  {
    q: "How much are Unitary Patent renewal fees?",
    a: "They follow the 'True Top 4' schedule, roughly EUR 35 in year 2 rising to about EUR 4,855 in year 20, totalling around EUR 35,500 over the full term for all participating states.",
  },
  {
    q: "What happens if an annuity is missed?",
    a: "There is a six-month grace period with a 50% surcharge at the EPO. After that the application is deemed withdrawn, and only further processing or re-establishment of rights can revive it.",
  },
  {
    q: "How much are EPO annuity fees in total?",
    a: "From the 3rd year to grant, EPO renewal fees run from about EUR 610 to EUR 2,015 per year. A typical application granted in year 5 or 6 pays roughly EUR 2,400 to EUR 3,800 in EPO annuities before grant.",
  },
  {
    q: "Are EU patent fees the same in every country?",
    a: "No. There is no single EU patent fee after grant unless you request unitary effect. Classic validation means a separate national annuity in each validated state, and national schedules differ by a factor of three or more.",
  },
  {
    q: "Who pays European patent annuities for a US company?",
    a: "Either an annuity service provider, which tracks and pays fees per patent per country for a service fee, or your European Patent Attorney through a central agent network. For small portfolios the attorney route is usually cheaper and simpler.",
  },
];


export const Route = createFileRoute("/tools/epo-renewal-fees")({
  head: () => ({
    meta: [
      { title: "EPO Renewal Fees 2026: Annuity Schedule & Calculator" },
      {
        name: "description",
        content:
          "Official EPO renewal fees and Unitary Patent annuities for 2026, plus a free calculator comparing classic validation with the Unitary Patent for US companies.",
      },
      { property: "og:title", content: "EPO Renewal Fees 2026: Schedule & Calculator" },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Official EPO annuity schedule, Unitary Patent renewal fees and a calculator comparing both routes over the full 20-year term.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bauerip.com/tools/epo-renewal-fees" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "EPO Renewal Fee Calculator",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bauerip.com/tools/epo-renewal-fees",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Official EPO renewal fees and a calculator comparing classic European patent validation annuities with Unitary Patent renewal fees.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: RenewalCalculator,
});

const DEFAULT_SELECTION = ["DE", "FR", "GB", "IT", "ES"];

function RenewalCalculator() {
  const [selected, setSelected] = useState<string[]>(DEFAULT_SELECTION);
  const [lastYear, setLastYear] = useState(20);
  const [includeValidation, setIncludeValidation] = useState(true);

  const result = useMemo(
    () => estimate({ selected, lastYear, includeValidation }),
    [selected, lastYear, includeValidation],
  );

  const toggle = (code: string) =>
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );

  const diff = result.classicTotal - result.unitaryTotal;
  const unitaryCheaper = diff > 0;
  const maxYear = Math.max(
    1,
    ...result.perYear.map((p) => Math.max(p.classic, p.unitary)),
  );

  return (
    <div>
      <Section
        eyebrow="EPO fees hub"
        title="EPO Renewal Fees: Schedule, Annuities and Calculator"
        titleAs="h1"
        intro="Everything a US company needs on European patent renewal fees: the official EPO annuity schedule while the application is pending, Unitary Patent renewal fees after grant, and a calculator comparing classic country-by-country validation with the Unitary Patent."
      >
        <div className="mb-16 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-2xl text-foreground">
              Official EPO renewal fees (pending applications)
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Paid to the EPO each year from the third year until grant, due on the last day of the
              anniversary month of filing.
            </p>
            <table className="mt-5 w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 font-medium">Renewal year</th>
                  <th className="py-2 text-right font-medium">Official fee</th>
                </tr>
              </thead>
              <tbody>
                {EPO_PENDING_FEES.map((row) => (
                  <tr key={row.year} className="border-b border-border/50">
                    <td className="py-2 text-foreground">{row.year}</td>
                    <td className="py-2 text-right text-muted-foreground">{eur(row.fee)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-foreground">
              Unitary Patent renewal fees (after grant)
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              A single annual fee covering every participating member state — the "True Top 4"
              schedule.
            </p>
            <table className="mt-5 w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 font-medium">Year</th>
                  <th className="py-2 text-right font-medium">Renewal fee</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(UNITARY_PATENT_FEES).map(([year, fee]) => (
                  <tr key={year} className="border-b border-border/50">
                    <td className="py-2 text-foreground">Year {year}</td>
                    <td className="py-2 text-right text-muted-foreground">{eur(fee)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <h2 className="mb-8 font-serif text-2xl text-foreground">
          Compare classic validation with the Unitary Patent
        </h2>

        <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
          {/* Inputs */}
          <div className="space-y-8">
            <div>
              <p className="eyebrow mb-4 text-xs">1 · Target countries</p>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map((c) => {
                  const active = selected.includes(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggle(c.code)}
                      className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                        active
                          ? "border-foreground bg-foreground text-primary-foreground"
                          : "border-foreground/25 text-foreground hover:border-foreground"
                      }`}
                    >
                      {c.name}
                      {!c.unitary && (
                        <span className="ml-1.5 opacity-60">
                          ·non-UP
                          <span className="sr-only"> (not covered by the Unitary Patent)</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Countries marked <em>non-UP</em> sit outside the Unitary Patent territory and always
                need a separate national validation.
              </p>
            </div>

            <div>
              <label htmlFor="lastYear" className="eyebrow mb-4 block text-xs">
                2 · Keep the patent alive until year {lastYear}
              </label>
              <input
                id="lastYear"
                type="range"
                min={3}
                max={20}
                step={1}
                value={lastYear}
                onChange={(e) => setLastYear(Number(e.target.value))}
                className="w-full accent-[var(--accent)]"
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Year 3</span>
                <span>Year 20 (full term)</span>
              </div>
            </div>

            <div>
              <p className="eyebrow mb-4 text-xs">3 · Options</p>
              <label className="flex items-start gap-3 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeValidation}
                  onChange={(e) => setIncludeValidation(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Include one-off validation costs (local agent, translations). These are the
                  largest single saving of the Unitary Patent.
                </span>
              </label>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-secondary/40 p-6">
                <p className="eyebrow mb-2 text-xs">Classic validation</p>
                <p className="font-serif text-3xl text-foreground">{eur(result.classicTotal)}</p>
                <dl className="mt-4 space-y-1 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <dt>National annuities</dt>
                    <dd>{eur(result.classicAnnuities)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Validation / translation</dt>
                    <dd>{eur(result.classicValidation)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Countries</dt>
                    <dd>{result.covered}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-accent/40 bg-accent/5 p-6">
                <p className="eyebrow mb-2 text-xs">Unitary Patent (+ non-UP states)</p>
                <p className="font-serif text-3xl text-foreground">{eur(result.unitaryTotal)}</p>
                <dl className="mt-4 space-y-1 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <dt>Renewal fees</dt>
                    <dd>{eur(result.unitaryAnnuities)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Validation / translation</dt>
                    <dd>{eur(result.unitaryValidation)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Separate validations</dt>
                    <dd>{result.nonUnitary.length}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="rounded-lg border border-border p-6">
              <p className="text-sm text-muted-foreground">
                {result.covered === 0 ? (
                  "Select at least one country to compare the two routes."
                ) : unitaryCheaper ? (
                  <>
                    Over {lastYear} years, the Unitary Patent route is cheaper by{" "}
                    <strong className="text-foreground">{eur(diff)}</strong> for this footprint.
                    The Unitary Patent generally wins once you want{" "}
                    <strong className="text-foreground">four or more</strong> participating states.
                  </>
                ) : (
                  <>
                    Over {lastYear} years, classic validation is cheaper by{" "}
                    <strong className="text-foreground">{eur(Math.abs(diff))}</strong>. With a small
                    footprint you pay less by validating only where you actually trade — and you keep
                    the option to drop countries later.
                  </>
                )}
              </p>
            </div>

            <div>
              <p className="eyebrow mb-4 text-xs">Annual fee profile</p>
              <div className="space-y-2">
                {result.perYear.map((row) => (
                  <div key={row.year} className="flex items-center gap-3 text-xs">
                    <span className="w-10 shrink-0 text-muted-foreground">Y{row.year}</span>
                    <div className="flex-1 space-y-1">
                      <div className="h-2 rounded-full bg-secondary">
                        <div
                          className="h-2 rounded-full bg-foreground/70"
                          style={{ width: `${(row.classic / maxYear) * 100}%` }}
                        />
                      </div>
                      <div className="h-2 rounded-full bg-secondary">
                        <div
                          className="h-2 rounded-full bg-accent"
                          style={{ width: `${(row.unitary / maxYear) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="w-20 shrink-0 text-right text-muted-foreground">
                      {eur(row.classic)}
                    </span>
                    <span className="w-20 shrink-0 text-right text-muted-foreground">
                      {eur(row.unitary)}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Dark bar = classic validation · brass bar = Unitary Patent route.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 rounded-lg border border-border bg-secondary/20 p-6">
          <h2 className="mb-4 font-serif text-xl text-foreground">Assumptions & fee schedules</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-foreground">What each input means</h3>
              <dl className="mt-3 space-y-3 text-sm text-muted-foreground">
                <div>
                  <dt className="font-medium text-foreground">Target countries</dt>
                  <dd>
                    The countries where you would validate a granted European patent. Countries
                    marked <em>non-UP</em> are outside the Unitary Patent territory and always need a
                    separate national validation, even if you choose the Unitary Patent route.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Keep alive until year</dt>
                  <dd>
                    The last patent year you intend to pay renewal fees. Year 3 is the first EPO
                    renewal year; year 20 is the maximum patent term. Dropping countries earlier is
                    one of the main flexibilities of the classic route.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Include validation costs</dt>
                  <dd>
                    One-off fees for local agents, translations and formalities required to turn a
                    granted European patent into a national title. The Unitary Patent removes most of
                    these inside its territory.
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h3 className="text-sm font-medium text-foreground">Fee schedules used</h3>
              <dl className="mt-3 space-y-3 text-sm text-muted-foreground">
                <div>
                  <dt className="font-medium text-foreground">Unitary Patent renewals</dt>
                  <dd>
                    Official annual renewal fees published by the Unified Patent Court (UPC), paid
                    in euros. The same single fee covers all participating EU member states.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">EPO national renewal fees</dt>
                  <dd>
                    Indicative national annuity fees for each selected country, modelled from each
                    national patent office's published schedule. Actual fees change by year and are
                    paid in local currency, then converted to euros.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Validation costs</dt>
                  <dd>
                    Approximate one-off costs for local representation and translation in each
                    country. These are estimates; the exact cost depends on patent length, language
                    pair and local attorney rates.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <p className="mt-12 max-w-3xl text-sm text-muted-foreground">
          Indicative official fees only, in euros, for planning purposes. National annuities,
          translation requirements and attorney charges vary by country and by year, and exchange
          rates move. Ask for a written estimate before budgeting.
        </p>
      </Section>

      <Section
        className="border-t border-border"
        eyebrow="What the numbers hide"
        title="Cost is only half of the decision"
      >
        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              t: "One basket, one court",
              d: "A Unitary Patent lives or dies as a whole before the UPC. Classic validations can be attacked only country by country.",
            },
            {
              t: "You cannot prune later",
              d: "With classic validation you drop expensive countries as the product matures. Unitary renewal fees are all-or-nothing.",
            },
            {
              t: "Coverage you may not need",
              d: "The Unitary Patent covers participating states you might never sell in — worth it only if the fee curve still beats your real footprint.",
            },
          ].map((item) => (
            <div key={item.t}>
              <h3 className="font-serif text-xl text-foreground">{item.t}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <CTAButton
            variant="accent"
            href={mailto({
              subject: "European renewal fee strategy",
              body: "Hello Karel,\n\nI used the renewal fee calculator and would like a written estimate.\n\n— Company:\n— Technology:\n— Target countries:\n— Expected patent lifetime:\n",
            })}
          >
            Get a written estimate
          </CTAButton>
          <Link
            to="/services/european-patent-attorney"
            className="inline-flex items-center justify-center rounded-full border border-foreground/25 px-6 py-3 text-sm font-medium tracking-wide text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
          >
            European Patent Attorney services →
          </Link>
        </div>
      </Section>

      <Section
        className="border-t border-border"
        eyebrow="After grant"
        title="National annuities, validation strategy and portfolio pruning"
        intro="EPO renewal fees stop at grant. From that day a classic European patent becomes a bundle of national patents, and each validated country charges its own maintenance fee on its own deadline, in its own currency."
      >
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground">Where the money goes.</strong> Germany has one of
              the steepest national schedules, especially after year 10. France, Italy and the
              United Kingdom are moderate. Spain, Poland and most smaller states are low. Validation
              itself is due within three months of the mention of grant in most contracting states,
              and several still require translations.
            </p>
            <p>
              <strong className="text-foreground">Country mix drives lifetime cost.</strong> Most
              efficient US-owned portfolios validate in a core group — Germany, France, the United
              Kingdom and the Netherlands, sometimes Italy and Spain — rather than everywhere the
              patent could be validated. Coverage you never enforce is coverage you pay for twenty
              times.
            </p>
            <p>
              <strong className="text-foreground">The Unitary Patent changes the arithmetic.</strong>{" "}
              Its renewal fees are set to approximate the combined cost of the four most-validated
              states, as one payment, with no translations and no national agents. Beyond four or
              five countries it usually wins; for one or two countries selective national validation
              is cheaper. It does not cover the United Kingdom, Switzerland, Norway or Turkey.
            </p>
            <p>
              <strong className="text-foreground">Annuities are back-loaded.</strong> Years 15 to 20
              carry the highest fees, which is exactly when many US companies should be pruning
              low-value jurisdictions. Review the portfolio every year against current revenue,
              licensing value and product lifecycle — not against the original filing plan.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
            <h3 className="font-serif text-xl text-foreground">
              Workflow for a newly granted European patent
            </h3>
            <ol className="mt-5 space-y-4 text-sm text-muted-foreground">
              {[
                "Before grant, decide the target country mix or whether to request unitary effect.",
                "Within three months of grant, validate in the selected states or file the request for unitary effect.",
                "Set up annuity tracking with reminders ahead of every national due date.",
                "Review the portfolio annually and drop countries that no longer earn their fee.",
                "Reconcile renewal spend against licensing revenue and enforcement activity.",
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-serif text-accent-text">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm text-muted-foreground">
              The most common failures are validating too broadly, missing the hand-off from EPO
              renewals to national annuities, and tracking European and USPTO maintenance deadlines
              as if they followed the same rules. They do not.
            </p>
          </div>
        </div>
      </Section>


      <Section
        className="border-t border-border"
        eyebrow="EPO renewal fees FAQ"
        title="Questions US patent owners ask"
      >
        <dl className="grid gap-8 md:grid-cols-2">
          {FAQS.map((f) => (
            <div key={f.q}>
              <dt className="font-serif text-xl text-foreground">{f.q}</dt>
              <dd className="mt-3 text-muted-foreground leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-12 text-sm text-muted-foreground">
          Background reading:{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "european-patent-maintenance-fees-guide" }}
            className="underline underline-offset-4"
          >
            European patent maintenance fees explained
          </Link>
          .
        </p>
      </Section>
    </div>
  );
}

